import { Label } from '@/components/base/label'
import { Icon } from '@/components/common/icons/Icon'
import { SliderWithTicks } from '@/components/SliderWithTicks'
import { DEBOUNCE_SLIDER_TIMEOUT_MS } from '@/helpers/constants'
import { validateMinMax } from '@/helpers/utils'
import { useOptimisticUpdateWithPromise } from '@/hooks/useOptimisticUpdateWithPromise'
import { useRoomStoreActions } from '@/stores/room'
import { useCallback, useId } from 'react'

// range of input sensitivity set from the SDK/server
const NOISE_GATE_RANGE = {
  MAX: 100,
  MIN: 0,
} as const

interface SliderSetNoiseGate {
  disabled: boolean
  memberId: string
  noiseGate: number
}

export const SliderSetNoiseGate = ({
  disabled,
  memberId,
  noiseGate,
}: SliderSetNoiseGate) => {
  const noiseGateId = useId()

  const { setNoiseGate } = useRoomStoreActions()

  const handleUpdateCallback = useCallback(
    (value: number) => {
      if (validateMinMax(value, NOISE_GATE_RANGE.MIN, NOISE_GATE_RANGE.MAX)) {
        return setNoiseGate({ memberId, value })
      } else {
        throw new Error('Invalid noise gate value')
      }
    },
    [memberId, setNoiseGate],
  )

  const { optimisticValue, handleUpdate } = useOptimisticUpdateWithPromise(
    noiseGate,
    handleUpdateCallback,
    DEBOUNCE_SLIDER_TIMEOUT_MS,
  )

  const handleValueChange = (value: number[]) => {
    const valueToUpdate = value.at(0)
    if (valueToUpdate !== undefined) {
      handleUpdate(valueToUpdate)
    }
  }

  return (
    <>
      <div className="flex items-center gap-x-3 py-4 pr-2">
        <Icon size="xl" tag="graphic-eq" />
        <Label htmlFor={noiseGateId}>Noise Gate</Label>
      </div>
      <SliderWithTicks
        disabled={disabled}
        id={noiseGateId}
        max={NOISE_GATE_RANGE.MAX}
        min={NOISE_GATE_RANGE.MIN}
        onValueChange={handleValueChange}
        step={1}
        stops={9}
        value={[optimisticValue]}
      />
    </>
  )
}
